import React from 'react';
import { useWindowSize } from 'react-use';
import cn from 'classnames';

import ICON_IS_OFF from 'assets/images/online-booking/Offline.svg';
import Button from 'components/form/Button.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import Badge from 'components/generic/Badge.component';
import Icon from 'components/generic/Icon.component';
import Tooltip from 'components/generic/Tooltip.component';
import TRANSFER_POLICY_TEXT from 'constants/pos/stripeTransferPolicyText.const';
import STRIPE_STATUS from 'constants/stripeStatus.const';
import MOBILE_VIEWPORT from 'constants/ui/mobileViewport.const';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import createNodeKey from 'utils/react/createNodeKey.util';

import ServiceList from 'settings/page-elements/dropdown/ServiceList';

import CN from './DropdownService.module.scss';

const getTransferPolicyText = (transferPolicy, t) => {
  const DISPLAY_POLICY_TEXT = {
    [TRANSFER_POLICY_TEXT.allTransfers]: t('onlineBooking.generalSettings.dropdownService.badge.takingTips'),
    [TRANSFER_POLICY_TEXT.splitCommissions]: t('onlineBooking.generalSettings.dropdownService.badge.commissionTips'),
    [TRANSFER_POLICY_TEXT.tipOnly]: t('onlineBooking.generalSettings.dropdownService.badge.noTakings'),
    [TRANSFER_POLICY_TEXT.noTransfer]: t('onlineBooking.generalSettings.dropdownService.badge.none'),
  };

  return DISPLAY_POLICY_TEXT[transferPolicy] || t('onlineBooking.generalSettings.dropdownService.badge.unknownPolicy');
};

const DropdownService = (props) => {
  const {
    stripeStatus,
    generalName,
    subName,
    isSelf,
    stylistFace,
    serviceCount,
    isOff,
    singleCompleted,
    isEditMode,
    onCheckboxClick,
    onSwitcherClick,
    category_services: categoryServices,
    stylist,
    stylistId,
    transferPolicy,
    isOneStylist,
    onEditTransferPolicy,
  } = props;

  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { width: windowWidth } = useWindowSize();

  const isMobileViewport = windowWidth <= MOBILE_VIEWPORT;
  const tooltipPosition = isMobileViewport ? 'right' : 'top';
  const tooltipPositionStart = isMobileViewport ? 'topLeft' : 'topRight';

  const componentClasses = cn({
    [CN.component]: true,
    [CN.darkMode]: isDarkMode,
    [CN.offline]: isOff,
    [CN.isOpen]: !isEditMode,
  });
  const colButtonsClasses = cn({
    [CN.colButtons]: true,
    [CN.isMobileOrder]: true,
  });
  const mobileOrderClasses = cn({
    [CN.mobileOrder]: true,
    [CN.isMobileOrder]: true,
  });
  const mobileLabelClasses = cn({
    [CN.mobileLabel]: true,
    [CN.isHidden]: true,
  });

  const keyOrder = categoryServices.map((c) => c.name);

  return (
    <div data-bem="DropdownService--settings" className={componentClasses}>
      <div className={CN.stylistHeader} data-bem="DropdownService__stylistHeader">
        <div className={CN.colName}>
          <Tooltip
            variant={tooltipPosition}
            width="sm"
            content={
              isOff
                ? t('onlineBooking.generalSettings.dropdownService.tooltip1text1')
                : t('onlineBooking.generalSettings.dropdownService.tooltip1text2')
            }
          >
            <img className={CN.avatar} alt={generalName} src={isOff ? ICON_IS_OFF : stylistFace} />
          </Tooltip>
          <div className={CN.stylistInfoWrap}>
            <div className={CN.stylist}>
              <div className={CN.name}>{generalName}</div>
              <div className={CN.romeName}>{subName}</div>
              <div className={CN.isSelf}>{isSelf}</div>
            </div>
            <div className={CN.mobileButtonEditDone}>
              <Button variant="primaryForce" width="medium" onClick={props.onToggle}>
                <span className={CN.gapSm}>
                  {isEditMode ? t('common.buttons.editUpp') : t('common.buttons.doneUpp')}
                </span>
              </Button>
            </div>
          </div>
        </div>

        <div data-bem="DropdownService__column-services" className={CN.colService}>
          <div className={CN.mobileOrder}>
            <div className={CN.mobileLabel}>{t('onlineBooking.generalSettings.dropdownService.bookebleServices')}</div>
            {serviceCount}
          </div>
          <div className={CN.desktopButtonEditDone}>
            <Button variant="primaryForce" width="medium" onClick={props.onToggle}>
              <span className={CN.gapSm}>{isEditMode ? t('common.buttons.editUpp') : t('common.buttons.doneUpp')}</span>
            </Button>
          </div>
        </div>

        <div data-bem="DropdownService__column-status" className={colButtonsClasses}>
          <div className={mobileOrderClasses}>{props.children}</div>
          <div
            className={cn({
              [CN.alignStart]: true,
              [CN.isMobileOrder]: true,
            })}
          >
            <div className={mobileLabelClasses}>{t('onlineBooking.generalSettings.dropdownService.stripAccount')}</div>
            {singleCompleted ? (
              <Badge variant="success">
                <div className={CN.buttonGap}>
                  <Icon variant="stripeBlue" />
                </div>
                {t('onlineBooking.generalSettings.dropdownService.badge.completed')}
              </Badge>
            ) : (
              <div>
                {stripeStatus === STRIPE_STATUS.noAccount && (
                  <Badge variant="draft" name={t('onlineBooking.generalSettings.dropdownService.badge.noAccount')} />
                )}
                {stripeStatus === STRIPE_STATUS.complete && (
                  <Badge variant="success" name={t('onlineBooking.generalSettings.dropdownService.badge.complete')} />
                )}
                {stripeStatus === STRIPE_STATUS.incomplete && (
                  <Badge
                    variant="attention"
                    name={t('onlineBooking.generalSettings.dropdownService.badge.incomplete')}
                  />
                )}
                {stripeStatus === STRIPE_STATUS.restricted && (
                  <Tooltip
                    variant="topRight"
                    width="md"
                    content={t('onlineBooking.generalSettings.dropdownService.tooltip2')}
                  >
                    <Badge
                      variant="warning"
                      name={t('onlineBooking.generalSettings.dropdownService.badge.restricted')}
                    />
                  </Tooltip>
                )}
              </div>
            )}
          </div>
        </div>

        <div
          data-bem="DropdownService__column-tips"
          className={cn({
            [CN.colTips]: true,
            [CN.isMobileOrder]: true,
          })}
        >
          <div className={mobileOrderClasses}>
            <div className={mobileLabelClasses}>{t('onlineBooking.generalSettings.dropdownService.takingsTips')}</div>

            {isOneStylist ? (
              <Badge variant="slickLight">
                <div className={CN.badgeText}>
                  {t('onlineBooking.generalSettings.dropdownService.badge.takingTips')}
                </div>
              </Badge>
            ) : (
              <Badge variant="slickLight">
                <div className={CN.badgeText}>{getTransferPolicyText(transferPolicy, t)}</div>
              </Badge>
            )}
          </div>
          {/* NOTE:for single stylist, all their money goes to the main stripe account and they dont need a separate staff account,
          so that’s why we dont show the setup button , EDIT SETUP and badge is hardcoded*/}
          <div
            className={cn({
              [CN.alignStart]: true,
              [CN.isMobileOrder]: true,
            })}
          >
            {!isOneStylist && (
              <div>
                {stripeStatus !== STRIPE_STATUS.complete && stripeStatus !== STRIPE_STATUS.restricted ? (
                  <Tooltip
                    variant={tooltipPositionStart}
                    width="md"
                    content={t('onlineBooking.generalSettings.dropdownService.tooltip3')}
                  >
                    <Button variant="secondary" onClick={alwaysNoop} disabled>
                      <span className={CN.buttonEdit}>
                        {t('onlineBooking.generalSettings.dropdownService.editSetup')}
                      </span>
                    </Button>
                  </Tooltip>
                ) : (
                  <Button variant="secondary" onClick={onEditTransferPolicy}>
                    <span className={CN.buttonEdit}>
                      {t('onlineBooking.generalSettings.dropdownService.editSetup')}
                    </span>
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div data-bem="DropdownService__servicesContainer" className={CN.servicesContainer}>
        {stripeStatus === STRIPE_STATUS.restricted && !isEditMode && (
          <div className={CN.alertContainer}>
            <AlertPanel tag="DropdownService__alert" variant="warning">
              <Icon variant="warning" />
              <div className={CN.alertText}>{t('onlineBooking.generalSettings.dropdownService.alertMessage')}</div>
            </AlertPanel>
          </div>
        )}
        {!isEditMode &&
          keyOrder.map((categoryName, index) => {
            const category = stylist.categories?.[categoryName] ?? null;
            if (category) {
              return (
                <ServiceList
                  stylistId={stylistId}
                  categoryName={categoryName}
                  services={category}
                  category_services={categoryServices}
                  index={index}
                  key={createNodeKey({ tag: categoryName, unique: `${stylistId}-${index}` })}
                  onCheckboxClick={onCheckboxClick}
                  onSwitcherClick={onSwitcherClick}
                />
              );
            }
            return null;
          })}
      </div>
    </div>
  );
};

export default DropdownService;
